import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { MotionBox } from "@rusha/shared-theme";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ShieldIcon from "@mui/icons-material/Shield";
import CodeIcon from "@mui/icons-material/Code";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

const MotionTypography = motion.create(Typography);

interface ValueProp {
  icon: typeof RocketLaunchIcon;
  title: string;
  description: string;
  colorKey: "primary" | "secondary" | "success" | "warning" | "info" | "error";
}

const valueProps: ValueProp[] = [
  {
    icon: RocketLaunchIcon,
    title: "Ship in Minutes, Not Days",
    description:
      "Skip the YAML and terraform. Choose a template, click deploy, get a live URL. Your team writes code, we handle the infrastructure.",
    colorKey: "primary",
  },
  {
    icon: CloudQueueIcon,
    title: "Deploy Anywhere",
    description:
      "AWS, GCP, Azure, or self-hosted Kubernetes. Same templates, zero config changes. Rusha abstracts away the cloud specifics.",
    colorKey: "secondary",
  },
  {
    icon: ShowChartIcon,
    title: "Know Your Costs",
    description:
      "Real-time per-system billing. See exactly what each service costs. No surprises at month-end, no DevOps spreadsheets.",
    colorKey: "success",
  },
  {
    icon: ShieldIcon,
    title: "Enterprise Security",
    description:
      "SOC2-ready architecture. Org-scoped access, audit trails, and hardened auth. Your CTO will sleep better at night.",
    colorKey: "warning",
  },
  {
    icon: CodeIcon,
    title: "GitHub-First Workflow",
    description:
      "Push to dev, we deploy. Webhooks trigger builds automatically. No CI/CD configuration, no GitHub Actions to manage.",
    colorKey: "info",
  },
  {
    icon: AccountTreeIcon,
    title: "Production Templates",
    description:
      "From React+FastAPI to Airbyte+dbt+Trino lakehouse. Pre-tested stacks for web apps, data pipelines, and analytics.",
    colorKey: "error",
  },
];

const Features: React.FC = () => {
  const theme = useTheme();

  const getColor = (colorKey: ValueProp["colorKey"]) => {
    switch (colorKey) {
      case "primary":
        return theme.palette.primary.main;
      case "secondary":
        return theme.palette.secondary.main;
      case "success":
        return theme.palette.success.main;
      case "warning":
        return theme.palette.warning.main;
      case "info":
        return theme.palette.info.main;
      case "error":
        return theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <Box
      id="features"
      component="section"
      sx={{
        py: 20,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle background pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <MotionBox
          sx={{ textAlign: "center", mb: 14 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <MotionTypography
            variant="h2"
            sx={{
              mb: 3,
              fontWeight: 800,
              color: "text.primary",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Engineering Teams Choose Rusha
          </MotionTypography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              mx: "auto",
              color: "text.secondary",
              fontWeight: 400,
              fontSize: "1.25rem",
            }}
          >
            We handle the infrastructure so your team can focus on building
            product
          </Typography>
        </MotionBox>

        <Grid container spacing={4}>
          {valueProps.map((prop, index) => {
            const IconComponent = prop.icon;
            const color = getColor(prop.colorKey);
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <MotionBox
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Box
                    sx={{
                      p: 4,
                      height: "100%",
                      borderRadius: 3,
                      background: "background.paper",
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: alpha(color, 0.4),
                        boxShadow: `0 12px 40px ${alpha(color, 0.12)}`,
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2.5,
                        background: alpha(color, 0.1),
                      }}
                    >
                      <IconComponent sx={{ fontSize: 24, color }} />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h3"
                      sx={{
                        color: "text.primary",
                        fontWeight: 600,
                        mb: 1.5,
                        fontSize: "1.1rem",
                      }}
                    >
                      {prop.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.7,
                        fontSize: "0.9rem",
                      }}
                    >
                      {prop.description}
                    </Typography>
                  </Box>
                </MotionBox>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default Features;
